/**
 * Assembles the `GateContext` that `evaluatePublishGate` needs.
 *
 * The gate is a pure function over a guide plus everything that guide depends
 * on, which is exactly why it can run identically in the browser and in the
 * `onPublish` function. That purity has a cost here: the browser has to go and
 * fetch the dependencies. This hook does that once per opened guide and then
 * re-evaluates on every keystroke against the in-memory copy, so typing stays
 * cheap and the panel stays live.
 *
 * The same fetch backs the dashboards, which need every guide and the gate's
 * verdict on each of them. That is why `guides` is held whole rather than
 * reduced to the three fields the gate itself reads, and why `categories` is
 * loaded here: two hooks fetching the same six collections would be two caches
 * to keep honest and twice the reads on a screen that shows one number.
 */

import { useEffect, useMemo, useState } from "react";
import { useDataSource } from "@firecms/core";
import type {
    Category,
    Claim,
    GateContext,
    Guide,
    Person,
    Source
} from "@zennopay/schema";

import {
    authorsCollection,
    categoriesCollection,
    claimsCollection,
    reviewersCollection,
    sourcesCollection
} from "../collections/supporting";
import { guidesCollection } from "../collections/guides";
import { STATIC_SITE_ROUTES } from "./knownRoutes";

export interface ReferenceData {
    authors: Map<string, Person>;
    reviewers: Map<string, Person>;
    claims: Claim[];
    sources: Map<string, Source>;
    /** Every guide, whole: slug and title collisions, and every dashboard. */
    guides: Guide[];
    categories: Category[];
}

const EMPTY: ReferenceData = {
    authors: new Map(),
    reviewers: new Map(),
    claims: [],
    sources: new Map(),
    guides: [],
    categories: []
};

/** Firestore document id wins over any stored `id`, matching the exporter. */
const withId = <T,>(entity: { id: string; values: any }): T =>
    ({ ...entity.values, id: entity.id }) as T;

const byId = <T extends { id: string }>(records: T[]): Map<string, T> =>
    new Map(records.map((record) => [record.id, record]));

export interface GateReferenceState {
    data: ReferenceData;
    loading: boolean;
    error?: Error;
}

export function useGateReferenceData(): GateReferenceState {
    const dataSource = useDataSource();
    const [state, setState] = useState<GateReferenceState>({ data: EMPTY, loading: true });

    useEffect(() => {
        let cancelled = false;

        const fetchAll = async () => {
            const [authors, reviewers, claims, sources, guides, categories] = await Promise.all([
                dataSource.fetchCollection<Person>({ path: "authors", collection: authorsCollection as any }),
                dataSource.fetchCollection<Person>({ path: "reviewers", collection: reviewersCollection as any }),
                dataSource.fetchCollection<Claim>({ path: "claims", collection: claimsCollection as any }),
                dataSource.fetchCollection<Source>({ path: "sources", collection: sourcesCollection as any }),
                dataSource.fetchCollection<Guide>({ path: "guides", collection: guidesCollection as any }),
                dataSource.fetchCollection<Category>({ path: "categories", collection: categoriesCollection as any })
            ]);
            if (cancelled) return;
            setState({
                loading: false,
                data: {
                    authors: byId(authors.map((entity) => withId<Person>(entity))),
                    reviewers: byId(reviewers.map((entity) => withId<Person>(entity))),
                    claims: claims.map((entity) => withId<Claim>(entity)),
                    sources: byId(sources.map((entity) => withId<Source>(entity))),
                    guides: guides.map((entity) => withId<Guide>(entity)),
                    categories: categories.map((entity) => withId<Category>(entity))
                }
            });
        };

        fetchAll().catch((error: Error) => {
            if (!cancelled) setState({ data: EMPTY, loading: false, error });
        });

        return () => {
            cancelled = true;
        };
    }, [dataSource]);

    return state;
}

/**
 * Every public route the site is known to serve.
 *
 * The static list plus one entry per guide slug. Pulled out of `gateContextFor`
 * because the broken-link dashboard builds it once for the whole collection
 * rather than once per guide, and rebuilding an identical set a hundred times
 * is the difference between a dashboard that opens and one that stutters.
 */
export function knownRoutesFor(guides: Array<Pick<Guide, "slug">>): Set<string> {
    const routes = new Set<string>(STATIC_SITE_ROUTES);
    for (const guide of guides) {
        if (guide.slug) routes.add(`/guides/${guide.slug}/`);
    }
    return routes;
}

/**
 * Builds the context for one guide from the loaded reference data.
 *
 * `sources` is deliberately the guide's *attached* sources rather than every
 * source in the CMS: the gate uses that set to decide whether a cited claim
 * points at a source this guide actually carries, and handing it the whole
 * library would make `claim.orphaned` unreachable.
 *
 * Pure, so the dashboards can evaluate the gate over every guide without a
 * hook per row.
 */
export function gateContextFor(
    entityId: string | undefined,
    values: Partial<Guide>,
    data: ReferenceData,
    knownRoutes?: Set<string>
): GateContext {
    const guide = {
        ...values,
        id: entityId ?? "",
        claimIds: values.claimIds ?? [],
        sourceIds: values.sourceIds ?? []
    } as Guide;

    const others = data.guides.filter((other) => other.id !== entityId);

    const routes = new Set(knownRoutes ?? knownRoutesFor(data.guides));
    if (guide.slug) routes.add(`/guides/${guide.slug}/`);

    return {
        guide,
        author: guide.authorId ? data.authors.get(guide.authorId) : undefined,
        reviewer: guide.reviewerId ? data.reviewers.get(guide.reviewerId) : undefined,
        claims: data.claims,
        sources: guide.sourceIds
            .map((id) => data.sources.get(id))
            .filter((source): source is Source => source !== undefined),
        existingSlugs: new Set(
            others.map((other) => other.slug).filter((slug): slug is string => Boolean(slug))
        ),
        existingTitles: new Set(
            others
                .map((other) => other.title?.trim().toLowerCase())
                .filter((title): title is string => Boolean(title))
        ),
        knownRoutes: routes
    };
}

export function useGateContext(
    entityId: string | undefined,
    values: Partial<Guide>,
    data: ReferenceData
): GateContext {
    return useMemo(
        () => gateContextFor(entityId, values, data),
        [entityId, values, data]
    );
}
